import { IconCheck } from "@tabler/icons-react";
import { Button, Flex, Slider, Text, Textarea } from "@mantine/core";
import { useInputState } from "@mantine/hooks";
import { type FormEvent, useCallback, useState } from "react";

import { AnalyticsEvents } from "../../constants/analytics-events";
import type { Track } from "../../hooks/useAnalytics";
import type { StaticTextTranslator } from "../../utils";

import styles from "./InterviewFeedback.module.css";

interface InterviewFeedbackProps {
  translate: StaticTextTranslator;
  track: Track;
  onFeedbackSubmitted(): void;
}

// 0–10 slider marks.
const SLIDER_MARKS = Array.from({ length: 11 }, (_, value) => ({ value }));

// Rating the slider *shows* before anyone touches it. Deliberately not the
// initial `score` state.
//
// SRN-798: seeding `score` to this value (the AS-1866 decision) meant a
// candidate who typed "thank you" and submitted without ever touching the
// slider was recorded as having chosen 5. Once 3.0 became the primary flow that
// took recorded 5s from ~3% to ~51% and dragged the average candidate feedback
// score from ~8.94 to ~7.00 — roughly 99% of the decline — while the average of
// every non-5 score stayed put. It measured our own default, not satisfaction.
//
// 2.x behaved correctly and is the shape we match: the thumb sits at 5, the
// state stays empty, and an untouched submission reports no score at all. See
// screener_frontend's `screener-feedback.tsx`.
const DEFAULT_SCORE = 5;

/** Whether a submitted score reflects a real choice, for analytics. */
enum ScoreSource {
  UserSelected = "user_selected",
  UntouchedDefault = "untouched_default",
}

/**
 * Post-interview candidate feedback for AI Screener 3.0 (AS-1866). Ports the v2
 * screener_frontend ScreenerFeedback experience into the interviewer_frontend
 * completion flow and fires the same "Feedback Submitted" Segment event so
 * Lisa's funnel dashboards stay unified across interview generations.
 */
export function InterviewFeedback({
  translate,
  track,
  onFeedbackSubmitted,
}: InterviewFeedbackProps) {
  // No initial value: `undefined` until the candidate moves the slider. The
  // thumb still renders at DEFAULT_SCORE via the Slider's own `defaultValue`.
  const [score, setScore] = useState<number>();
  const [feedback, setFeedback] = useInputState("");

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      const trimmedFeedback = feedback.trim();
      if (!trimmedFeedback) return;

      // A candidate who drags the slider and settles on 5 has chosen 5, and
      // that is indistinguishable from the default by value alone — which is
      // why the interaction is reported alongside it rather than inferred from
      // the number downstream.
      const isScoreTouched: boolean = score !== undefined;

      track(AnalyticsEvents.FeedbackSubmitted, {
        feedback_type: "post_screener_session",
        feedback_text: trimmedFeedback,
        score: score ?? null,
        score_touched: isScoreTouched,
        score_source: isScoreTouched
          ? ScoreSource.UserSelected
          : ScoreSource.UntouchedDefault,
      });
      onFeedbackSubmitted();
    },
    [feedback, onFeedbackSubmitted, score, track],
  );

  return (
    <Flex
      className={styles.interviewFeedback}
      component="form"
      direction="column"
      onSubmit={onSubmit}
      data-testid="interview-feedback"
    >
      <Text>{translate("How was your interview?")}</Text>
      <Slider
        color="#15BFA3"
        defaultValue={DEFAULT_SCORE}
        label={(value) => `${value}/10`}
        marks={SLIDER_MARKS}
        max={10}
        min={0}
        my={14}
        onChange={setScore}
        step={1}
        thumbSize={32}
      />
      <Text>
        {translate(
          "Is there anything you want to share about your experience?",
        )}
      </Text>
      <Textarea
        autosize
        maxRows={4}
        minRows={4}
        mt={12}
        onChange={setFeedback}
        placeholder={translate("Your feedback")}
        size="md"
      />
      <Button
        className={styles.submitButton}
        color="#15BFA3"
        disabled={!feedback.trim()}
        leftSection={<IconCheck size={18} />}
        mt={21}
        type="submit"
      >
        {translate("Submit feedback")}
      </Button>
    </Flex>
  );
}
